// src/components/MapboxMap.js
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import Map, {
  NavigationControl,
  GeolocateControl,
  Source,
  Layer,
} from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { useLocation } from "../../contexts/LocationContext";
import { useSettings } from "../../contexts/SettingsContext";
import { detectSystemTheme, watchSystemTheme } from "../../utils/theme";
import { AnimatePresence, motion } from "framer-motion";
import AvatarMarker from "./AvatarMarker";
import ClusterMarker from "./ClusterMarker";
import ColocatedMarker from "./ColocatedMarker";
import SettingsControl from "./SettingsControl";
import useClusters from "../../hooks/useClusters";
import { createCircle, computeRadiusBounds } from "../../utils/createCircle";
import { devLog, devWarn, logError } from "../../utils/logger";
import useViewport from "../../hooks/useViewport";
import { MAP_STYLES } from "../../utils/mapStyles";

const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || process.env.VITE_MAPBOX_TOKEN;

// Ensure Mapbox controls render above our z-indexed markers (1-30 range)
if (
  typeof document !== "undefined" &&
  !document.getElementById("mapbox-ctrl-z")
) {
  const style = document.createElement("style");
  style.id = "mapbox-ctrl-z";
  style.textContent = `
    .mapboxgl-ctrl-top-right,
    .mapboxgl-ctrl-top-left,
    .mapboxgl-ctrl-bottom-right,
    .mapboxgl-ctrl-bottom-left {
      z-index: 40 !important;
    }
  `;
  document.head.appendChild(style);
}

export default function MapboxMap({
  notes = [],
  selectedNote,
  initialViewState,
  committedRadius,
  onMarkerClick,
  className = "",
  searchRadius = 1,
  showRadiusCircle = false,
  settingsPopover,
  fitBoundsRequest,
}) {
  const {
    userLocation,
    locationError,
    isLoading: isLocationLoading,
    requestLocation,
    setLocationError,
  } = useLocation();

  // Get user's settings
  const { settings } = useSettings();
  const showMyLocationOnMap = settings?.showMyLocationOnMap ?? false;
  const unit = settings?.units || "miles";

  const { vw } = useViewport();
  const isMobile = vw < 768;

  // Determine map style based on current theme
  const mapStyle = useMemo(() => {
    let theme = settings?.theme || "light";

    // If auto, detect system theme
    if (theme === "auto") {
      theme = detectSystemTheme();
    }

    return theme === "dark" ? MAP_STYLES.dark : MAP_STYLES.light;
  }, [settings?.theme]);

  // Check if Mapbox token exists
  if (!MAPBOX_TOKEN) {
    logError("Mapbox Access token is missing.");
    setLocationError({
      type: "mapbox_token",
      message:
        "Mapbox access token is missing. We are working on a fix. Try again later.",
      retryAction: null,
    });
    return null;
  }

  // Refs so handleMapLoad can read the latest prop values without needing
  // them in its dependency array (which would recreate it on every change).
  const committedRadiusRef = useRef(committedRadius);
  committedRadiusRef.current = committedRadius;
  const unitRef = useRef(unit);
  unitRef.current = unit;

  const [viewState, setViewState] = useState(() => {
    /**
     * When initialViewState uses bounds, the Map is in controlled mode so we
     * seed viewState with center coordinates. handleMapLoad will compute
     * the exact zoom via cameraForBounds once the map container exists.
     * Use zoom 0 for "See all notes" (committedRadius > 5000) to minimise
     * the visible jump; otherwise a mid-range placeholder.
     */
    if (initialViewState?.bounds) {
      return {
        longitude: initialViewState.longitude,
        latitude: initialViewState.latitude,
        zoom: committedRadius > 5000 ? 0 : 10,
      };
    }

    if (initialViewState) {
      return initialViewState;
    }

    if (userLocation?.longitude && userLocation?.latitude) {
      return {
        longitude: userLocation.longitude,
        latitude: userLocation.latitude,
        zoom: 15,
      };
    }
    /**
     * Default to San Francisco
     */
    return {
      longitude: -122.4194,
      latitude: 37.7749,
      zoom: 10,
    };
  });

  // Track map loaded state to prevent errors during mouse events
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // Zoom gesture zone state (mobile only)
  const [isZoneDragging, setIsZoneDragging] = useState(false);
  const dragRef = useRef({ startY: 0, startZoom: 0, active: false });
  const zoneRef = useRef(null);

  // Settings control state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [expandedColocatedId, setExpandedColocatedId] = useState(null);
  const settingsButtonRef = useRef(null);
  const settingsCloseRef = useRef(null);
  const popoverRef = useRef(null);

  /**
   * Refs for managing mock geolocation
   */
  const geolocationControlRef = useRef(null);
  const lastLocationId = useRef(null);
  const mapRef = useRef(null);

  /**
   * Watch for system theme changes when in auto mode
   * Dynamically recalculates and applies the correct map style
   */
  useEffect(() => {
    if (settings?.theme !== "auto") {
      return;
    }

    // Set up listener for system theme changes
    const cleanup = watchSystemTheme((newTheme) => {
      // newTheme is 'light' or 'dark' from the system
      const newMapStyle =
        newTheme === "dark" ? MAP_STYLES.dark : MAP_STYLES.light;

      // Apply the new style to the map
      const map = mapRef.current?.getMap();
      if (map && map.isStyleLoaded()) {
        map.setStyle(newMapStyle);
      }
    });

    return cleanup;
  }, [settings?.theme]);

  /**
   * Create circle GeoJSON for radius visualization
   * Only recreates when user location, search radius, or unit changes
   */
  const radiusCircleGeoJSON = useMemo(() => {
    if (!userLocation?.latitude || !userLocation?.longitude || !searchRadius) {
      return null;
    }

    return createCircle(
      userLocation.latitude,
      userLocation.longitude,
      searchRadius,
      unit,
    );
  }, [userLocation?.latitude, userLocation?.longitude, searchRadius, unit]);

  /**
   * Setup mock geolocation to feed our LocationContext data to GeolocateControl
   */
  useEffect(() => {
    devLog("Setting up mock geolocation");

    /**
     * Creating mock geolocation. Instead of calling the browser geolocation API,
     * it will use our LocationContext. In order to have uniform location in all the app pages,
     * The location fetching will be in one place: LocationContext
     */
    const mockGeolocation = {
      getCurrentPosition: (successCallback, errorCallback) => {
        devLog(
          "Mock getCurrentPosition called at",
          new Date(Date.now()).toLocaleTimeString(),
        );

        if (userLocation) {
          /**
           * Create a fake GPS position that matches GeolocationPosition from the LocationContext
           */
          const position = {
            coords: {
              latitude: userLocation.latitude,
              longitude: userLocation.longitude,
              accuracy: userLocation.accuracy || 10,
              altitude: null,
              altitudeAccuracy: null,
              heading: null,
              speed: null,
            },
            timestamp: userLocation.timestamp || Date.now(),
          };

          devLog("Mock geolocation returning:", position);
          /**
           * Calling the success callback with our fake position
           * Tricks the <GeolocateControl> component think GPS succeeded
           */
          successCallback(position);
        } else if (locationError) {
          // Convert our LocationContext error to GeolocationPositionError
          /**
           * If LocationContext has an error, convert it to the format <GeolocateControl> expects,
           * Then call the error callback
           */
          const error = {
            code:
              locationError.type === "location_permission"
                ? 1
                : locationError.type === "location_unavailable"
                  ? 2
                  : locationError.type === "location_timeout"
                    ? 3
                    : 0,
            message: locationError.message,
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3,
          };
          errorCallback?.(error);
        }
      },

      /**
       * This function is called when the trackUserLocation prop is true in the <GeolocateControl> component
       * For now, we will not be tracking the user
       * TODO: implement tracking based on user feedback
       */
      watchPosition: function (successCallback, errorCallback) {
        // For simplicity, just call getCurrentPosition once
        this.getCurrentPosition(successCallback, errorCallback);
        return 1;
      },

      /**
       * This function is called when trackUserLocation prop is true in the <GeolocateControl> component
       * For now, we will not be tracking the user
       */
      clearWatch: () => {
        devLog("Mock clearWatch called");
      },
    };

    /**
     * Replacing the browser's native navigator.geolocation with our fake one
     * When <GeolocateControl> calls navigator.geolocation.getCurrentPosition(),
     * it will get the LocationContext data instead of triggering real GPS
     */
    Object.defineProperty(window.navigator, "geolocation", {
      value: mockGeolocation,
      writable: false,
      configurable: true,
    });

    /**
     * Cleanup function to restore original geolocation when MapboxMap unmounts
     */
    return () => {
      if (window.originalGeolocation) {
        Object.defineProperty(window.navigator, "geolocation", {
          value: window.originalGeolocation,
          writable: false,
          configurable: true,
        });
      }
    };
  }, [userLocation, locationError]);

  // Clustering
  const { clusters, superclusterRef } = useClusters(
    notes,
    mapRef,
    viewState,
    selectedNote?.id ?? null,
  );

  // Safe marker click handler that checks if map is loaded
  const handleMarkerClick = useCallback(
    (note) => {
      if (!isMapLoaded) {
        devWarn("Map not ready, ignoring marker click");
        return;
      }
      onMarkerClick?.(note);
    },
    [isMapLoaded, onMarkerClick],
  );

  // Zoom into a cluster on click
  // Track a pending auto-expand for co-located clusters after flyTo completes
  const pendingColocExpandRef = useRef(null);

  const handleClusterClick = useCallback(
    (clusterId, longitude, latitude) => {
      if (!isMapLoaded || !superclusterRef.current) return;
      const expansionZoom =
        superclusterRef.current.getClusterExpansionZoom(clusterId);
      const targetZoom = Math.min(expansionZoom, 18);

      // If the target zoom will put us beyond Supercluster's maxZoom (16),
      // the cluster can't split further — it will become a co-located group.
      // Use the Supercluster cluster ID to match the co-located item's ID.
      if (targetZoom >= 17) {
        pendingColocExpandRef.current = `coloc-${clusterId}`;
      }

      const map = mapRef.current?.getMap();
      map?.flyTo({
        center: [longitude, latitude],
        zoom: targetZoom,
        duration: 500,
      });

      // After flyTo completes, auto-expand the co-located cluster if pending
      if (targetZoom >= 17) {
        map?.once("moveend", () => {
          if (pendingColocExpandRef.current) {
            setExpandedColocatedId(pendingColocExpandRef.current);
            pendingColocExpandRef.current = null;
          }
        });
      }
    },
    [isMapLoaded, superclusterRef],
  );

  // Zoom gesture zone — native DOM listeners to intercept before Mapbox
  useEffect(() => {
    const zone = zoneRef.current;
    if (!zone) return;

    function onDown(e) {
      e.preventDefault();
      e.stopPropagation();
      zone.setPointerCapture(e.pointerId);
      const map = mapRef.current?.getMap();
      if (!map) return;
      dragRef.current = {
        startY: e.clientY,
        startZoom: map.getZoom(),
        active: true,
      };
      setIsZoneDragging(true);
    }

    function onMove(e) {
      if (!dragRef.current.active) return;
      e.preventDefault();
      e.stopPropagation();
      const deltaY = dragRef.current.startY - e.clientY;
      const zoomDelta = deltaY / 80;
      const newZoom = Math.max(
        0,
        Math.min(20, dragRef.current.startZoom + zoomDelta),
      );
      setViewState((prev) => ({ ...prev, zoom: newZoom }));
    }

    function onUp(e) {
      if (!dragRef.current.active) return;
      e.stopPropagation();
      zone.releasePointerCapture(e.pointerId);
      dragRef.current.active = false;
      setIsZoneDragging(false);
    }

    zone.addEventListener("pointerdown", onDown);
    zone.addEventListener("pointermove", onMove);
    zone.addEventListener("pointerup", onUp);
    zone.addEventListener("pointercancel", onUp);

    return () => {
      zone.removeEventListener("pointerdown", onDown);
      zone.removeEventListener("pointermove", onMove);
      zone.removeEventListener("pointerup", onUp);
      zone.removeEventListener("pointercancel", onUp);
    };
  }, []);

  /**
   * Handling location updates from LocationContext
   * This useEffect will come in handy when we decide to track the user's location
   */
  // Whether the caller explicitly set a fixed center (no bounds).
  // When true, skip all auto-centering on user location so the map
  // stays where the caller positioned it (e.g. NotePage on a note).
  const hasFixedCenter = initialViewState && !initialViewState.bounds;

  useEffect(() => {
    if (hasFixedCenter) return; // Caller owns the center — don't override
    if (userLocation?.longitude && userLocation?.latitude) {
      devLog("Using location from context:", userLocation);

      const isFirstLocation = lastLocationId.current === null;

      /**
       * Trigger GeolocateControl only on the very first location acquisition
       * to activate the user location dot. Subsequent location updates are
       * fed to the dot via the mock geolocation watchPosition callback —
       * calling trigger() again would fly the map back to the user's position
       * and disrupt free panning/zooming.
       *
       * Don't set lastLocationId until trigger fires — if the map isn't loaded
       * yet, we need the next run (when isMapLoaded flips) to still see
       * isFirstLocation=true so the trigger actually fires.
       */
      if (
        isFirstLocation &&
        isMapLoaded &&
        geolocationControlRef.current
      ) {
        devLog("Triggering GeolocateControl with initial location");
        try {
          if (geolocationControlRef.current && mapRef.current?.getMap()) {
            // trigger() internally calls map.fitBounds(maxZoom:18) which
            // changes map.transform without updating _renderTransform,
            // causing markers to offset from tiles in controlled mode.
            // No-op camera methods so only the blue dot activates.
            const map = mapRef.current.getMap();
            const _fitBounds = map.fitBounds;
            const _flyTo = map.flyTo;
            const _jumpTo = map.jumpTo;
            map.fitBounds = () => map;
            map.flyTo = () => map;
            map.jumpTo = () => map;
            geolocationControlRef.current.trigger();
            map.fitBounds = _fitBounds;
            map.flyTo = _flyTo;
            map.jumpTo = _jumpTo;
            devLog("Successfully triggered GeolocateControl");
          } else {
            devWarn("GeolocateControl or map not ready yet");
          }
        } catch (error) {
          devWarn("Failed to trigger GeolocateControl: ", error);
        }

        // Mark first location as handled — only after trigger fired
        lastLocationId.current = `${userLocation.latitude}-${userLocation.longitude}`;

        // Center map on first acquisition
        setViewState((prev) => ({
          ...prev,
          longitude: userLocation.longitude,
          latitude: userLocation.latitude,
        }));
      }
    }
  }, [userLocation, isMapLoaded, hasFixedCenter]);

  // Track whether the initial fitBounds has fired (skip first committedRadius effect)
  const isFirstCommit = useRef(true);
  // Track previous committedRadius to detect user-initiated slider changes
  const prevCommittedRadiusRef = useRef(committedRadius);

  const handleMapLoad = useCallback(() => {
    // Set the FINAL viewport in one shot using committedRadius (via ref)
    // so there's no visible zoom transition before markers appear.
    // committedRadius may differ from initialViewState.bounds when
    // "See all notes" is on — using it here means the committedRadius
    // effect's first run is a no-op (same viewport already applied).
    const map = mapRef.current?.getMap();
    if (map && initialViewState?.bounds) {
      const cr = committedRadiusRef.current;
      const u = unitRef.current;
      const lat = initialViewState.latitude;
      const lng = initialViewState.longitude;

      if (cr > 5000) {
        map.fitBounds([[-180, -85], [180, 85]], {
          padding: 15,
          duration: 0,
        });
      } else {
        map.fitBounds(
          computeRadiusBounds(lat, lng, cr, u),
          { padding: 15, maxZoom: 18, duration: 0 },
        );
      }
    }
    setIsMapLoaded(true);
  }, [initialViewState]);

  const handleMapError = useCallback(
    (evt) => {
      logError("Mapbox error:", evt.error);
      setIsMapLoaded(false);

      /**
       * Check for token-related errors
       */
      const errorMessage = evt.error?.message || "Unknown error";
      if (
        errorMessage.toLowerCase().includes("token") ||
        errorMessage.toLowerCase().includes("unauthorized") ||
        errorMessage.toLowerCase().includes("access")
      ) {
        setLocationError({
          type: "mapbox_token",
          message:
            "Invalid or missing Mapbox access token. We are working on a fix. Try again later.",
          retryAction: null,
        });
      } else {
        setLocationError({
          type: "mapbox_load",
          message: `Map failed to load: ${errorMessage}`,
          retryAction: () => window.location.reload(),
        });
      }
    },
    [setLocationError],
  );

  // Fit the map viewport to the radius circle.
  // Fires on: initial load (isMapLoaded), user slider change, unit change.
  //
  // Imperative camera methods (fitBounds/flyTo) change map.transform without
  // updating react-map-gl's _renderTransform, causing markers to render at
  // wrong positions relative to tiles. Only use them for user-initiated
  // slider changes where the smooth animation justifies the brief 1-frame
  // lag. All other triggers use setViewState which keeps both transforms
  // in sync via react-map-gl's useLayoutEffect.
  useEffect(() => {
    // Skip the initial mount (handleMapLoad already called fitBounds)
    if (isFirstCommit.current) {
      isFirstCommit.current = false;
      return;
    }

    if (
      !userLocation?.latitude ||
      !userLocation?.longitude ||
      !committedRadius
    ) {
      return;
    }

    const map = mapRef.current?.getMap();
    if (!map || !isMapLoaded) return;

    const radiusChanged = prevCommittedRadiusRef.current !== committedRadius;
    prevCommittedRadiusRef.current = committedRadius;

    if (radiusChanged) {
      // User changed slider → smooth animated transition (imperative)
      if (committedRadius > 5000) {
        map.flyTo({
          center: [userLocation.longitude, userLocation.latitude],
          zoom: 0,
          duration: 600,
        });
      } else {
        map.fitBounds(
          computeRadiusBounds(
            userLocation.latitude, userLocation.longitude,
            committedRadius, unit,
          ),
          { padding: 15, maxZoom: 18, duration: 600 },
        );
      }
    } else {
      // isMapLoaded or unit changed → instant via setViewState (no mismatch)
      if (committedRadius > 5000) {
        setViewState((prev) => ({
          ...prev,
          longitude: userLocation.longitude,
          latitude: userLocation.latitude,
          zoom: 0,
        }));
      } else {
        const camera = map.cameraForBounds(
          computeRadiusBounds(
            userLocation.latitude, userLocation.longitude,
            committedRadius, unit,
          ),
          { padding: 15, maxZoom: 18 },
        );
        if (camera) {
          setViewState({
            longitude: camera.center.lng,
            latitude: camera.center.lat,
            zoom: camera.zoom,
          });
        }
      }
    }
  }, [committedRadius, unit, isMapLoaded]);

  // Fit bounds to specific coordinates when requested externally (legacy bulk nearby notifications)
  useEffect(() => {
    if (!fitBoundsRequest || !isMapLoaded) return;
    const map = mapRef.current?.getMap();
    if (!map || fitBoundsRequest.length === 0) return;

    if (fitBoundsRequest.length === 1) {
      // Single point — fly to it
      map.flyTo({
        center: [fitBoundsRequest[0][0], fitBoundsRequest[0][1]],
        zoom: 15,
        duration: 600,
      });
    } else {
      // Multiple points — fit all in viewport
      const lngs = fitBoundsRequest.map((p) => p[0]);
      const lats = fitBoundsRequest.map((p) => p[1]);
      map.fitBounds(
        [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ],
        { padding: 60, maxZoom: 16, duration: 600 },
      );
    }
  }, [fitBoundsRequest, isMapLoaded]);

  // Close settings popover when clicking outside
  useEffect(() => {
    if (!isSettingsOpen) return;

    function handleClickOutside(e) {
      const popoverEl = popoverRef.current;
      const buttonEl = settingsButtonRef.current;
      if (
        popoverEl &&
        !popoverEl.contains(e.target) &&
        buttonEl &&
        !buttonEl.contains(e.target)
      ) {
        settingsCloseRef.current?.();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isSettingsOpen]);

  return (
    <div className={className || "relative w-full h-full"}>
      <Map
        ref={mapRef}
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        onDragStart={() => {
          if (expandedColocatedId && !pendingColocExpandRef.current)
            setExpandedColocatedId(null);
        }}
        onZoomStart={() => {
          if (expandedColocatedId && !pendingColocExpandRef.current)
            setExpandedColocatedId(null);
        }}
        onLoad={handleMapLoad}
        onError={handleMapError}
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: "100%", height: "100%" }}
        mapStyle={mapStyle}
        projection="mercator"
        logoPosition={isMobile ? "bottom-left" : "bottom-right"}
      >
        {/* Controls — hide +/- on mobile since the zoom gesture zone replaces them */}
        {!isMobile && <NavigationControl position="top-right" />}
        {showMyLocationOnMap && (
          <GeolocateControl
            ref={geolocationControlRef}
            position="top-right"
            showUserLocation={true}
            trackUserLocation={false}
            showAccuracyCircle={true}
            fitBoundsOptions={{ maxZoom: 18 }}
          />
        )}
        {settingsPopover && (
          <SettingsControl
            position="top-right"
            onOpenChange={setIsSettingsOpen}
            buttonRef={settingsButtonRef}
            closeRef={settingsCloseRef}
          />
        )}

        {/* Mapbox native location marker is used automatically */}

        {/* Radius circle visualization */}
        {showRadiusCircle && radiusCircleGeoJSON && isMapLoaded && (
          <Source id="radius-circle" type="geojson" data={radiusCircleGeoJSON}>
            <Layer
              id="radius-circle-fill"
              type="fill"
              paint={{
                "fill-color": "#6366F1", // primary-500
                "fill-opacity": 0.08,
              }}
            />
            {searchRadius <= 5000 && (
              <Layer
                id="radius-circle-outline"
                type="line"
                paint={{
                  "line-color": "#6366F1", // primary-500
                  "line-width": 2,
                  "line-opacity": 0.35,
                }}
              />
            )}
          </Source>
        )}

        {/* Markers (clustered) — z-ordered so lower-on-screen markers render on top */}
        {/* Z-index: read 1-14, unread 15-29, selected 35 — all below sidebar/nav (z-40) */}
        {isMapLoaded &&
          clusters.map((item) => {
            const lng =
              item.type === "note" ? item.note?.longitude : item.longitude;
            const lat =
              item.type === "note" ? item.note?.latitude : item.latitude;
            const map = mapRef.current?.getMap();

            let zIndex = 1;
            if (map && lng != null && lat != null) {
              try {
                const projected = map.project([lng, lat]);
                const canvas = map.getCanvas();
                const maxY = canvas?.height || 1000;
                // Normalize screen y (0..maxY) to z-index range 1..14
                zIndex = Math.max(
                  1,
                  Math.min(14, Math.round((projected.y / maxY) * 13) + 1),
                );
              } catch {
                // map not ready
              }
            }

            // Unread markers get +14 offset (range 15-29), above read markers (1-14)
            const hasUnread =
              item.type === "note"
                ? !item.note?.isOwner && !item.note?.isViewedByCurrentUser
                : !item.allViewed;
            if (hasUnread) zIndex += 14;

            const isSelected =
              item.type === "note" && selectedNote?.id === item.note?.id;
            if (isSelected) zIndex = 35;

            if (item.type === "cluster") {
              return (
                <ClusterMarker
                  key={`cluster-${item.id}`}
                  cluster={item}
                  onClick={handleClusterClick}
                  zIndex={zIndex}
                />
              );
            }

            if (item.type === "colocated") {
              const isThisExpanded = expandedColocatedId === item.id;
              const containsSelected = selectedNote && item.notes?.some((n) => n.id === selectedNote.id);
              return (
                <ColocatedMarker
                  key={item.id}
                  cluster={item}
                  isExpanded={isThisExpanded}
                  onExpand={() => setExpandedColocatedId(item.id)}
                  onCollapse={() => setExpandedColocatedId(null)}
                  onNoteSelect={handleMarkerClick}
                  zIndex={isThisExpanded || containsSelected ? 35 : zIndex}
                />
              );
            }

            const note = item.note;
            return (
              <AvatarMarker
                key={note.id ?? `${note.longitude},${note.latitude}`}
                note={note}
                onClick={handleMarkerClick}
                isSelected={isSelected}
                zIndex={zIndex}
              />
            );
          })}
      </Map>

      {/* Mobile-only zoom gesture zone — right edge between controls and create button */}
      <div
        ref={zoneRef}
        className="absolute right-0 w-10 z-[35] md:hidden flex items-center justify-center"
        style={{ touchAction: "none", top: "25%", bottom: "25%" }}
      >
        {/* Thin track line with grip dots */}
        <div className="pointer-events-none flex flex-col items-center justify-center h-full">
          <div
            className="rounded-full"
            style={{
              width: 2,
              height: "100%",
              backgroundColor: isZoneDragging
                ? "rgba(99, 102, 241, 0.35)"
                : "rgba(156, 163, 175, 0.12)",
              transition: "background-color 200ms",
            }}
          />
        </div>
      </div>

      {/* Settings popover — positioned relative to the native Mapbox control button */}
      <AnimatePresence>
        {isSettingsOpen && settingsPopover && (
          <SettingsPopover
            popover={settingsPopover}
            buttonRef={settingsButtonRef}
            popoverRef={popoverRef}
          />
        )}
      </AnimatePresence>

      {/* Simple status indicator for location loading */}
      {isLocationLoading && (
        <div className="absolute top-4 left-4 bg-surface-elevated rounded-lg shadow-lg px-3 py-2">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-info border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-text-secondary">
              Finding your location...
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Settings popover panel — positioned next to the native Mapbox settings button.
 * Uses the button ref to calculate vertical alignment dynamically.
 */
function SettingsPopover({ popover, buttonRef, popoverRef }) {
  const [isEditingRadius, setIsEditingRadius] = useState(false);
  const [editValue, setEditValue] = useState("");
  const radiusInputRef = useRef(null);
  const [topOffset, setTopOffset] = useState(0);

  useEffect(() => {
    const btn = buttonRef?.current;
    if (!btn) return;

    // Get button position relative to the map container
    const mapContainer = btn.closest(".mapboxgl-map")?.parentElement;
    if (mapContainer) {
      const mapRect = mapContainer.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      setTopOffset(btnRect.top - mapRect.top);
    }
  }, [buttonRef]);

  return (
    <motion.div
      ref={popoverRef}
      initial={{ opacity: 0, x: 6, scale: 0.98 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 6, scale: 0.98 }}
      transition={{ type: "spring", stiffness: 240, damping: 22 }}
      className="absolute z-[35] w-56 sm:w-64 max-w-[calc(100vw-6rem)] rounded-2xl bg-surface-elevated border border-border p-4 shadow-xl"
      style={{ top: topOffset, right: 49 }}
    >
      {popover.showSeeAllToggle && (
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs text-text-secondary">See all notes</span>
          <button
            onClick={() => popover.onSeeAllChange(!popover.seeAllValue)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              popover.seeAllValue ? "bg-primary" : "bg-surface-muted"
            }`}
            aria-label="See all notes"
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface-elevated transition-transform ${
                popover.seeAllValue ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
      )}

      <div
        className={popover.seeAllValue ? "opacity-40 pointer-events-none" : ""}
      >
        <div className="flex items-center justify-between text-xs text-text-secondary">
          <span>Nearby radius</span>
          <span className="inline-flex items-baseline text-text-primary font-medium">
            <input
              ref={radiusInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={isEditingRadius ? editValue : String(Math.round(popover.value))}
              onFocus={(e) => {
                setEditValue(String(Math.round(popover.value)));
                setIsEditingRadius(true);
                e.target.select();
              }}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^\d+$/.test(v)) setEditValue(v);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.target.blur();
                } else if (e.key === "Escape") {
                  setIsEditingRadius(false);
                }
              }}
              onBlur={() => {
                if (!isEditingRadius) return;
                const clamped = Math.min(
                  Math.max(Math.round(Number(editValue) || 1), popover.min),
                  popover.max,
                );
                popover.onChange(clamped);
                popover.onChangeCommitted?.(clamped);
                setIsEditingRadius(false);
              }}
              className={`bg-transparent text-right text-base md:!text-xs !leading-none font-medium outline-none cursor-pointer caret-primary border-b transition-colors font-[inherit] ${
                isEditingRadius
                  ? "border-primary"
                  : "border-dashed border-text-muted/30 hover:border-text-primary/50"
              }`}
              style={{ width: `${(String(isEditingRadius ? editValue : Math.round(popover.value)).length || 1) + 0.5}ch` }}
            />
            <span className="ml-0.5 text-[11px]">{popover.unit}</span>
          </span>
        </div>
        <input
          type="range"
          min={popover.min}
          max={popover.max}
          step={popover.step}
          value={popover.value}
          onChange={(e) => popover.onChange(Number(e.target.value))}
          onPointerUp={() => popover.onChangeCommitted?.()}
          onKeyUp={(e) => {
            if (
              ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
                e.key,
              )
            ) {
              popover.onChangeCommitted?.();
            }
          }}
          className="mt-3 w-full accent-primary"
        />
      </div>

      {popover.showToggle && (
        <>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-text-secondary">
              {popover.toggleLabel}
            </span>
            <button
              onClick={() => popover.onToggleChange(!popover.toggleValue)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                popover.toggleValue ? "bg-primary" : "bg-surface-muted"
              }`}
              aria-label={popover.toggleLabel}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface-elevated transition-transform ${
                  popover.toggleValue ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
          <div className="mt-1 text-[10px] text-text-muted/70">
            Change default in Settings
          </div>
        </>
      )}

      <div className="mt-2 text-[11px] text-text-muted">
        Drag or type to set how far notes are fetched.
      </div>
    </motion.div>
  );
}
